import { useEffect, useState } from 'react';
import type { VavClient } from '@vav/client-core';
import type { AttachmentRef } from '@vav/protocol';

interface Props {
  name: string;
  size?: number;
  group?: boolean;
  online?: boolean;
  /**
   * An encrypted picture to show instead of initials. Needs the client to
   * decrypt it, which is the point: the picture is a sealed blob the server
   * cannot open, and only someone holding the ref's key sees a face.
   */
  avatar?: AttachmentRef;
  client?: VavClient;
}

/**
 * The cool secondary palette. Avatars used to take any hue on the wheel, which
 * meant roughly one person in eight was assigned a gold indistinguishable from
 * brass -- and brass is supposed to be the one colour on screen that means
 * something. These stay clear of that hue entirely.
 *
 * Deliberately the same values in both themes: an avatar is a self-contained
 * chip, so it does not need to re-tune per ground the way text does. They are
 * light, so the initials on top are ink (8:1 or better on every entry).
 */
const SENDER_COLOURS = [
  '#8fb3c9',
  '#c1a0c9',
  '#9cbfa8',
  '#c9b78f',
  '#a9a6c9',
  '#c99f9f',
] as const;

/** Stable per name, so the same person keeps their colour without the server storing it. */
export function senderColourIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 4093;
  return hash % SENDER_COLOURS.length;
}

/**
 * Initials avatar with a colour derived from the name, so the same person keeps
 * the same colour on every device without the server storing anything.
 */
/**
 * The law: text colour on a tinted ground is computed, never hardcoded.
 *
 * Chen's rule, after black initials on dark chips: if the ground is dark the
 * text is white, and no one gets to eyeball it. The avatar grounds are the
 * sender palette deepened until white passes on both ends of the gradient
 * (4.97:1 at the light end, 6.83:1 at the dark) — the same move the brass
 * fills made. `initialsColourFor` re-derives the choice from luminance, so a
 * future palette change flips the text automatically instead of silently
 * shipping the next unreadable pair.
 */
function mixToInk(hex: string, keep: number): string {
  const c = (x: string) => x.replace('#', '').match(/../g)!.map((v) => parseInt(v, 16));
  const [a, b] = [c(hex), c('#14161c')];
  return '#' + a.map((v, i) => Math.round(v * keep + b[i] * (1 - keep)).toString(16).padStart(2, '0')).join('');
}

function luminance(hex: string): number {
  const c = hex.replace('#', '').match(/../g)!.map((x) => {
    const v = parseInt(x, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/**
 * Sharpened by Chen: the text on a chip is always light, full stop. When a
 * ground is too pale to carry light text, it is the GROUND that deepens —
 * flipping the text to black was the old behaviour and it looked wrong every
 * time it fired. `ensureDark` walks a colour toward ink until white passes.
 */
export function initialsColourFor(_ground: string): string {
  return '#ffffff';
}

function ensureDark(hex: string): string {
  let c = hex;
  for (let i = 0; i < 8 && luminance(c) > 0.32; i++) c = mixToInk(c, 0.82);
  return c;
}

const AVATAR_GROUNDS = SENDER_COLOURS.map((c) => ensureDark(mixToInk(c, 0.55)));

export function Avatar({ name, size = 46, group, online, avatar, client }: Props) {
  const base = AVATAR_GROUNDS[senderColourIndex(name)];
  const initials = group ? '👥' : initialsOf(name);
  const [url, setUrl] = useState<string>();

  /**
   * Decrypt on demand, cache by object URL, fall back to initials on any
   * failure. Initials are always rendered underneath, so a slow decrypt shows a
   * person rather than a hole.
   */
  useEffect(() => {
    if (!avatar || !client) {
      setUrl(undefined);
      return;
    }
    let cancelled = false;
    let objectUrl: string | undefined;
    void (async () => {
      try {
        const bytes = await client.downloadAttachment(avatar);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: avatar.mime }));
        setUrl(objectUrl);
      } catch {
        // A picture that cannot be fetched or decrypted is just initials.
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(undefined);
    };
  }, [avatar?.mediaId, client]);

  return (
    <span className="avatarWrap" style={{ width: size, height: size }}>
      <span
        className="avatar"
        style={{
          width: size,
          height: size,
          fontSize: size * (group ? 0.5 : 0.38),
          color: initialsColourFor(base),
          background: `linear-gradient(140deg, ${base}, color-mix(in srgb, ${base} 85%, #14161c))`,
        }}
        aria-hidden="true"
      >
        {url ? <img className="avatar__img" src={url} alt="" /> : initials}
      </span>
      {online !== undefined && (
        <i className={`presenceDot ${online ? 'is-online' : ''}`} aria-hidden="true" />
      )}
    </span>
  );
}

function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return [...words[0]].slice(0, 2).join('');
  return [...words[0]][0] + [...words[1]][0];
}

